/**
 * Authentication and Authorization Error System
 *
 * Typed error classes to replace magic string error codes throughout the application.
 * Provides better debugging, context preservation, and recovery strategies.
 *
 * @module AuthErrors
 */

import { LOADING_STEPS } from './Constants/Constants'

/**
 * Error codes used throughout the authentication system.
 * Centralized to prevent typos and ensure consistency.
 */
export const ERROR_CODES = {
    // Authentication errors
    AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
    MSAL_INITIALIZATION_FAILED: 'MSAL_INITIALIZATION_FAILED',
    TOKEN_REFRESH_FAILED: 'TOKEN_REFRESH_FAILED',

    // User context errors
    USER_NOT_FOUND: 'USER_NOT_FOUND',
    USER_CREATION_FAILED: 'USER_CREATION_FAILED',
    USER_CONTEXT_INVALID: 'USER_CONTEXT_INVALID',

    // Organization errors
    NO_ORGANISATION: 'NO_ORGANISATION',
    NO_USER_ORGANISATION: 'NO_USER_ORGANISATION',
    ORGANISATION_LOAD_FAILED: 'ORGANISATION_LOAD_FAILED',
    ORGANISATION_ACCESS_DENIED: 'ORGANISATION_ACCESS_DENIED',

    // Network and generic errors
    NETWORK_ERROR: 'NETWORK_ERROR',
    UNKNOWN_ERROR: 'UNKNOWN_ERROR',
}

/**
 * Base authentication error class with context preservation and recovery information.
 */
export class AuthenticationError extends Error {
    /**
     * @param {string} message - Human-readable error message
     * @param {string} code - Error code from ERROR_CODES
     * @param {Error} [originalError] - Original error that caused this error
     * @param {string} [loadingStep] - Loading step where error occurred
     * @param {boolean} [recoverable=true] - Whether error can be recovered from
     */
    constructor(message, code, originalError = null, loadingStep = null, recoverable = true) {
        super(message)
        this.name = 'AuthenticationError'
        this.code = code
        this.originalError = originalError
        this.loadingStep = loadingStep
        this.recoverable = recoverable
        this.timestamp = new Date().toISOString()

        // Preserve stack trace
        if (Error.captureStackTrace) {
            Error.captureStackTrace(this, AuthenticationError)
        }
    }

    /**
     * Get detailed error context for logging and debugging
     */
    getContext() {
        return {
            code: this.code,
            message: this.message,
            loadingStep: this.loadingStep,
            recoverable: this.recoverable,
            timestamp: this.timestamp,
            originalError: this.originalError
                ? {
                      name: this.originalError.name,
                      message: this.originalError.message,
                      stack: this.originalError.stack,
                  }
                : null,
        }
    }
}

/**
 * MSAL-specific authentication errors
 */
export class MsalError extends AuthenticationError {
    constructor(message, originalError = null) {
        super(
            message || 'MSAL authentication failed',
            ERROR_CODES.MSAL_INITIALIZATION_FAILED,
            originalError,
            LOADING_STEPS.AUTHENTICATION,
            true // MSAL errors are usually recoverable
        )
        this.name = 'MsalError'
    }
}

/**
 * User not found or creation failed errors
 */
export class UserNotFoundError extends AuthenticationError {
    constructor(originalError = null) {
        super(
            'User not found and could not be created',
            ERROR_CODES.USER_NOT_FOUND,
            originalError,
            LOADING_STEPS.USER_CONTEXT,
            true // Can often be resolved by recreating user
        )
        this.name = 'UserNotFoundError'
    }
}

/**
 * User context loading or validation errors
 */
export class UserContextError extends AuthenticationError {
    constructor(message, originalError = null) {
        super(
            message || 'Failed to load user context',
            ERROR_CODES.USER_CONTEXT_INVALID,
            originalError,
            LOADING_STEPS.USER_CONTEXT,
            true
        )
        this.name = 'UserContextError'
    }
}

/**
 * Organization-related errors
 */
export class OrganisationError extends AuthenticationError {
    constructor(message, code, originalError = null, recoverable = false) {
        super(message, code, originalError, LOADING_STEPS.ORGANISATION, recoverable)
        this.name = 'OrganisationError'
    }
}

/**
 * No organization found for user
 */
export class NoOrganisationError extends OrganisationError {
    constructor(originalError = null) {
        super(
            'User has no associated organization',
            ERROR_CODES.NO_USER_ORGANISATION,
            originalError,
            false // Usually requires admin intervention
        )
        this.name = 'NoOrganisationError'
    }
}

/**
 * Organization loading failed
 */
export class OrganisationLoadError extends OrganisationError {
    constructor(organisationId, originalError = null) {
        super(
            `Failed to load organization: ${organisationId}`,
            ERROR_CODES.ORGANISATION_LOAD_FAILED,
            originalError,
            true // Can often be retried
        )
        this.name = 'OrganisationLoadError'
        this.organisationId = organisationId
    }
}

/**
 * Network-related errors
 */
export class NetworkError extends AuthenticationError {
    constructor(message, originalError = null, loadingStep = LOADING_STEPS.AUTHENTICATION) {
        super(
            message || 'Network request failed',
            ERROR_CODES.NETWORK_ERROR,
            originalError,
            loadingStep, // Network errors should specify which step they occurred in
            true // Network errors are usually temporary
        )
        this.name = 'NetworkError'
    }
}

/**
 * Helper function to create appropriate error from RTK Query error
 *
 * @param {Object} rtkError - Error from RTK Query
 * @param {string} context - Context where error occurred
 * @returns {AuthenticationError} Appropriate typed error
 */
export const createErrorFromRtkError = (rtkError, context = 'unknown') => {
    const status = rtkError?.status
    const message = rtkError?.data?.message || rtkError?.error || 'Unknown error'

    // Map context string to loading step for error categorization.
    // Note: Non-tracked steps (organisationUser, materials, etc.) are included
    // for logging purposes even though their errors don't propagate to the UI.
    const getLoadingStepFromContext = (context) => {
        switch (context) {
            case 'user':
            case 'userContext':
                return LOADING_STEPS.USER_CONTEXT
            case 'organisation':
            case 'organization':
                return LOADING_STEPS.ORGANISATION
            case 'organisationUser':
            case 'organizationUser':
                return LOADING_STEPS.ORGANISATION_USER
            case 'materials':
                return LOADING_STEPS.MATERIALS
            case 'sheets':
                return LOADING_STEPS.SHEETS
            case 'cuttingTechs':
                return LOADING_STEPS.CUTTING_TECHS
            case 'rateTables':
                return LOADING_STEPS.RATE_TABLES
            case 'rates':
                return LOADING_STEPS.RATES
            default:
                return LOADING_STEPS.AUTHENTICATION
        }
    }

    const loadingStep = getLoadingStepFromContext(context)

    switch (status) {
        case 401:
            return new AuthenticationError(
                'Authentication failed - invalid or expired token',
                ERROR_CODES.AUTHENTICATION_ERROR,
                rtkError,
                LOADING_STEPS.AUTHENTICATION // 401 errors are always authentication-related
            )
        case 403:
            return new OrganisationError(
                'Access denied to organization',
                ERROR_CODES.ORGANISATION_ACCESS_DENIED,
                rtkError,
                false
            )
        case 404:
            if (context === 'user') {
                return new UserNotFoundError(rtkError)
            } else if (context === 'organisation') {
                return new OrganisationLoadError('unknown', rtkError)
            }
            return new AuthenticationError(
                `Resource not found: ${context}`,
                ERROR_CODES.UNKNOWN_ERROR,
                rtkError,
                loadingStep
            )
        case 'FETCH_ERROR':
        case 'TIMEOUT_ERROR':
            return new NetworkError(`Network error during ${context}`, rtkError, loadingStep)
        default:
            return new AuthenticationError(
                `Unexpected error during ${context}: ${message}`,
                ERROR_CODES.UNKNOWN_ERROR,
                rtkError,
                loadingStep
            )
    }
}

/**
 * Helper function to handle errors consistently across the application
 *
 * @param {Error} error - Error to handle
 * @param {Function} setStepError - Function to set loading step error
 * @param {Function} setStepErrorMessage - Function to set error message
 * @param {Function} [onError] - Optional additional error handler
 */
export const handleAuthenticationError = (error, setStepError, setStepErrorMessage, onError = null) => {
    // If it's already one of our typed errors, use it directly
    if (error instanceof AuthenticationError) {
        if (error.loadingStep) {
            setStepError(error.loadingStep, error.code, error)
            setStepErrorMessage(error.loadingStep, error.message)
        } else {
            // Fallback to authentication step if no loading step is specified
            console.warn('AuthenticationError without loadingStep, defaulting to AUTHENTICATION:', error)
            setStepError(LOADING_STEPS.AUTHENTICATION, error.code, error)
            setStepErrorMessage(LOADING_STEPS.AUTHENTICATION, error.message)
        }

        console.error('Authentication Error:', error.getContext())

        if (onError) {
            onError(error)
        }
        return
    }

    // For other errors, create a generic authentication error with proper loading step
    const authError = new AuthenticationError(
        error.message || 'An unexpected error occurred',
        ERROR_CODES.UNKNOWN_ERROR,
        error,
        LOADING_STEPS.AUTHENTICATION, // Always specify a loading step
        true // Unknown errors might be recoverable
    )

    setStepError(authError.loadingStep, authError.code, authError)
    setStepErrorMessage(authError.loadingStep, authError.message)

    console.error('Unhandled Error:', authError.getContext())

    if (onError) {
        onError(authError)
    }
}
